import type { User } from '@pbt/pbt-ui-components'

import type { ConstantEntity } from '~/api/graphql/generated/types'
import type { PrescriptionType } from '~/constants/prescription'
import { OrderType } from '~/constants/SOAPStates'
import { TimelineEntryType } from '~/constants/timelineConstants'
import type {
  DrugTimelineItem,
  Order,
  Prescription,
  PrescriptionTimelineItem,
  PrescriptionV2,
  PrescriptionWithTypeField,
  RxCardItem,
} from '~/types'
import { getIsRetailOrderLineItem } from '~/utils/invoiceUtils'
import { convertPrescriptionV2ToPrescription } from '~/utils/prescription'

import { getPrescriptionMostRecentDate } from './rxTracker/rxCardUtilities'

export interface RxCardConversionOptions {
  clientId?: string
  patientId?: string
}

type WithAdministrationRoute = { administrationRoute?: ConstantEntity | null }

/**
 * Converts V2 (GraphQL: nested objects) to flat Prescription if needed.
 * Timeline data is already flat; details page data from Redux is V2.
 */
function ensureFlatPrescription(
  prescription: Prescription | PrescriptionV2,
): Prescription {
  const r = prescription as WithAdministrationRoute
  const hasNestedRoute =
    r.administrationRoute != null && typeof r.administrationRoute === 'object'
  if (hasNestedRoute) {
    return (
      convertPrescriptionV2ToPrescription(prescription as PrescriptionV2) ??
      (prescription as Prescription)
    )
  }
  return prescription as Prescription
}

/**
 * Coalesce `prescriptionType ?? type` for card display / hooks. Omits a default so missing API data
 * stays unknown (avoids treating `undefined` as in-house downstream).
 */
function fulfillmentTypeForCard(
  p: PrescriptionWithTypeField,
): PrescriptionType | undefined {
  const v = p.prescriptionType ?? p.type
  if (v == null) return undefined
  return v as PrescriptionType
}

// ============================================================================
// ID Extraction Helpers (reduce casting noise, align with app pattern)
// ============================================================================

/**
 * Extracts ID from User object or string. Returns null if not found.
 * Handles API inconsistency where some fields return User object, others return string ID.
 */
function extractId(value: User | string | null | undefined): string | null {
  if (!value) return null
  return typeof value === 'string' ? value : (value.id ?? null)
}

/**
 * Extracts doctor ID from multiple possible sources (fallback chain).
 */
function extractDoctorId(sources: {
  doctor?: User | null
  doctorId?: string | null
  producerId?: string | null
  vet?: User | null
}): string | null {
  return (
    extractId(sources.doctor) ??
    sources.doctorId ??
    sources.producerId ??
    extractId(sources.vet) ??
    null
  )
}

/**
 * Extracts signer ID from multiple possible sources (fallback chain).
 */
function extractSignerId(sources: {
  doctorId?: string | null
  producerId?: string | null
  signer?: User | string | null
  signerId?: string | null
}): string | null {
  return (
    extractId(sources.signer) ??
    sources.signerId ??
    sources.doctorId ??
    sources.producerId ??
    null
  )
}

function isPrescriptionTimelineItem(
  item: PrescriptionTimelineItem | DrugTimelineItem,
): item is PrescriptionTimelineItem {
  return 'prescription' in item && item.prescription != null
}

function toUserFromVet(vet: DrugTimelineItem['vet']): User | undefined {
  if (!vet) return undefined
  return {
    id: vet.id,
    firstName: vet.firstName,
    lastName: vet.lastName,
    email: vet.email,
    active: vet.active,
    deleted: vet.deleted,
    invitationStatus: vet.invitationStatus as User['invitationStatus'],
    mobilePhone: vet.mobilePhone,
    photo: vet.photo,
  } as User
}

function withTitleOverlay(
  prescription: Prescription,
  overlay?: { masterName?: string | null; name?: string | null },
): Prescription {
  return {
    ...prescription,
    name: overlay?.name ?? prescription.name,
    masterName: overlay?.masterName ?? prescription.masterName,
  }
}

function makeRxCardItem(input: {
  businessId?: string | null
  cancellationReasonDetail?: string
  controlled?: boolean
  date: string
  invoiceId?: string | null
  prescription: Prescription
  signerId?: string
  soapId?: string | null
  subType: RxCardItem['subType']
  type?: RxCardItem['type']
  vet?: User
}): RxCardItem {
  const p = input.prescription
  return {
    type: input.type ?? TimelineEntryType.PRESCRIPTION,
    subType: input.subType,
    date: input.date,
    soapId: input.soapId ?? null,
    businessId: input.businessId ?? null,
    invoiceId: input.invoiceId ?? null,
    vet: input.vet,
    controlled: input.controlled ?? false,
    prescription: p,
    signerId: input.signerId ?? p.signerId ?? undefined,
    cancellationReasonDetail: input.cancellationReasonDetail,
  }
}

/**
 * Converts PrescriptionTimelineItem to RxCardItem.
 * Timeline prescriptions come from REST API and are already flat (prescriptionType at root).
 * Ensure refillExpirationDate is set when source has only quantityRefillExpirationDate (flat API).
 */
function fromPTI(pti: PrescriptionTimelineItem): RxCardItem {
  const rawPrescription = withTitleOverlay(pti.prescription, {
    name: pti.name,
    masterName: pti.masterName,
  })
  const rx = rawPrescription as {
    quantity?: number
    quantityRefillExpirationDate?: string | null
    quantityUnitId?: string
    quantityUnitResolved?: string
    refillExpirationDate?: string | null
    type?: string
  }
  const rxPrescription = rawPrescription as {
    totalQuantityUnitId?: string
    totalQuantityUnitResolved?: string
  }
  const isInventory = rx.type === OrderType.INVENTORY
  const inventoryQuantity =
    isInventory && rawPrescription.totalQuantity == null && rx.quantity != null
      ? { totalQuantity: rx.quantity }
      : undefined
  const inventoryUnitId =
    isInventory &&
    rxPrescription.totalQuantityUnitId == null &&
    rx.quantityUnitId != null
      ? { totalQuantityUnitId: rx.quantityUnitId }
      : undefined
  const resolvedUnitFromTimeline =
    rx.quantityUnitResolved ?? rxPrescription.totalQuantityUnitResolved
  const inventoryResolvedUnit =
    isInventory && resolvedUnitFromTimeline
      ? { totalQuantityUnitResolved: resolvedUnitFromTimeline }
      : undefined
  const merged = {
    ...rawPrescription,
    refillExpirationDate:
      rx.refillExpirationDate ?? rx.quantityRefillExpirationDate ?? undefined,
    ...inventoryQuantity,
    ...inventoryUnitId,
    ...inventoryResolvedUnit,
  }
  const cardFulfillmentType = fulfillmentTypeForCard(
    merged as PrescriptionWithTypeField,
  )
  const prescription = {
    ...merged,
    ...(cardFulfillmentType != null
      ? { prescriptionType: cardFulfillmentType }
      : {}),
  } as Prescription

  return makeRxCardItem({
    type: pti.type,
    subType: pti.subType,
    date: pti.date,
    soapId: pti.soapId,
    businessId: pti.businessId,
    invoiceId: pti.invoiceId,
    vet: pti.vet,
    controlled: pti.controlled,
    prescription,
    signerId: pti.signerId ?? undefined,
    cancellationReasonDetail: pti.cancellationReasonDetail ?? undefined,
  })
}

/**
 * Converts DrugTimelineItem (timeline "drug" row) to RxCardItem for use with BaseRxCard.
 * Drug: builds prescription from drug fields.
 */
function fromDrug(
  drug: DrugTimelineItem,
  options?: RxCardConversionOptions,
): RxCardItem | undefined {
  if (!drug.businessId || !drug.id) return undefined

  const vet = toUserFromVet(drug.vet)
  const displayName = drug.name ?? ''

  const prescription = {
    id: drug.id,
    businessId: drug.businessId,
    clientId: options?.clientId ?? null,
    patientId: options?.patientId ?? null,
    name: displayName,
    masterName: displayName,
    signerId: drug.vet?.id ?? null,
    doctor: vet ?? undefined,
    signer: vet ?? undefined,
    createdAt: drug.date,
    creationDate: drug.date,
    updatedAt: drug.date,
    modificationDate: drug.date,
    type: OrderType.INVENTORY,
    notes: drug.notes ?? undefined,
    totalQuantity: drug.quantity ?? null,
    totalQuantityUnitId: drug.quantityUnitId ?? undefined,
    soapId: drug.soapId ?? null,
    ...(drug.quantityUnitResolved != null &&
      drug.quantityUnitResolved !== '' && {
        totalQuantityUnitResolved: drug.quantityUnitResolved,
      }),
    invoiceId: drug.invoiceId ?? null,
    stateId: drug.statusId ?? null,
  } as Prescription

  return makeRxCardItem({
    subType: drug.subType,
    date: drug.date,
    soapId: drug.soapId ?? null,
    businessId: drug.businessId,
    invoiceId: drug.invoiceId ?? null,
    vet,
    controlled: drug.controlledSubstance,
    prescription,
    signerId: drug.vet?.id ?? undefined,
  })
}

/**
 * Converts Order (from unifiedOrder API / getUnifiedOrder) to RxCardItem for details page (ordered drug).
 * Extracts only IDs for doctor/signer - useDoctorInfoDisplay hook will fetch full User objects.
 */
export function orderToRxCardItem(
  order: Order | null | undefined,
  options?: RxCardConversionOptions,
): RxCardItem | undefined {
  if (order == null || typeof order !== 'object') return undefined

  const { lineItem } = order

  const lineItemIds = lineItem as { clientId?: string; patientId?: string }
  const lineItemStateId =
    (lineItem as { stateId?: string })?.stateId ??
    (lineItem as { logState?: { id?: string } })?.logState?.id

  const invoiceId =
    (order as { invoiceId?: string }).invoiceId ??
    (lineItem && getIsRetailOrderLineItem(lineItem)
      ? (lineItem as { invoice?: { id?: string } }).invoice?.id
      : (lineItem as { invoiceId?: string })?.invoiceId) ??
    undefined

  const displayName =
    (order as { drugMasterName?: string }).drugMasterName ??
    (order as { inventory?: { name?: string } }).inventory?.name ??
    (order as { globalInventory?: { name?: string } }).globalInventory?.name ??
    ''

  const basePrescription = order as Order & Partial<Prescription>
  const vet = (order as { signer?: User }).signer ?? undefined

  // Extract IDs using helpers (no User object fallback chains)
  const doctorId = extractDoctorId({
    doctor: basePrescription.doctor,
    doctorId: (order as { doctorId?: string | null }).doctorId,
    producerId: (lineItem as { producerId?: string })?.producerId,
  })

  const signerId = extractSignerId({
    signer:
      (order as { signer?: User | string }).signer ?? basePrescription.signer,
    signerId:
      (order as { signerId?: string | null }).signerId ??
      basePrescription.signerId,
    doctorId,
    producerId: (lineItem as { producerId?: string })?.producerId,
  })

  const orderDoctor =
    (order as { doctor?: User }).doctor ?? basePrescription.doctor
  const orderSigner =
    (order as { signer?: User }).signer ?? basePrescription.signer ?? vet

  const orderCardFulfillmentType = fulfillmentTypeForCard(
    basePrescription as PrescriptionWithTypeField,
  )

  const prescription = {
    ...basePrescription,
    id: (order as { id: string }).id,
    businessId:
      (order as { businessId?: string | null }).businessId ??
      basePrescription.businessId ??
      null,
    clientId:
      options?.clientId ??
      lineItemIds?.clientId ??
      (order as { clientId?: string | null }).clientId ??
      basePrescription.clientId ??
      null,
    patientId:
      options?.patientId ??
      lineItemIds?.patientId ??
      (order as { patientId?: string | null }).patientId ??
      basePrescription.patientId ??
      null,
    name: displayName || basePrescription.name || '',
    masterName:
      displayName || basePrescription.masterName || basePrescription.name || '',
    doctorId,
    signerId,
    doctor: orderDoctor ?? undefined,
    signer: orderSigner ?? undefined,
    creationDate:
      basePrescription.creationDate ??
      (order as { creationDate?: string }).creationDate,
    updatedAt:
      basePrescription.updatedAt ??
      (order as { modificationDate?: string }).modificationDate,
    stateId:
      basePrescription.stateId ??
      lineItemStateId ??
      (order as { inventoryLogStatusId?: string | null })
        .inventoryLogStatusId ??
      (order as { orderStateId?: string | null }).orderStateId ??
      (order as { stateId?: string | null }).stateId ??
      (order as { logState?: { id?: string } }).logState?.id ??
      null,
    ...(orderCardFulfillmentType != null
      ? { prescriptionType: orderCardFulfillmentType }
      : {}),
  } as Prescription

  const soapId =
    (order as { soapId?: string | null }).soapId ??
    (lineItem as { soapId?: string | null })?.soapId ??
    null

  return makeRxCardItem({
    subType: 'drug',
    date:
      (order as { creationDate?: string }).creationDate ??
      (order as { modificationDate?: string }).modificationDate ??
      '',
    soapId,
    businessId: (order as { businessId?: string | null }).businessId ?? null,
    invoiceId,
    vet,
    controlled: false,
    prescription,
    signerId:
      prescription.signerId ??
      (order as { signerId?: string }).signerId ??
      undefined,
  })
}

/**
 * Converts Prescription (from prescription API / store) to RxCardItem for details page (no timeline).
 * Accepts both entity Prescription and PrescriptionV2 (GraphQL schema) from the store.
 * Extracts only IDs for doctor/signer - useDoctorInfoDisplay hook will fetch full User objects.
 */
export function convertPrescriptionToRxCardItem(
  prescription: Prescription | PrescriptionV2,
): RxCardItem {
  // Details page gets PrescriptionV2 from Redux store (GraphQL format with nested objects).
  const p = ensureFlatPrescription(prescription)

  const vet: User | undefined =
    (p as { vet?: User }).vet ?? (p as { doctor?: User }).doctor

  const date = getPrescriptionMostRecentDate(p) ?? ''

  const stateId =
    (p as { stateId?: string }).stateId ??
    (p as { state?: { id?: string } }).state?.id ??
    null

  // Extract IDs using helpers (no User object building)
  const doctorId = extractDoctorId({
    doctor: (p as { doctor?: User }).doctor,
    doctorId: (p as { doctorId?: string }).doctorId,
    vet: (p as { vet?: User }).vet,
  })

  const signerId = extractSignerId({
    signer: (p as { signer?: User | string }).signer,
    signerId: (p as { signerId?: string }).signerId,
    doctorId,
  })

  const prescriptionDoctor = (p as { doctor?: User }).doctor ?? vet
  const { signer } = p as { signer?: User | string }
  const prescriptionSigner =
    signer != null && typeof signer === 'object' ? signer : undefined

  const invoiceId = p.invoiceId ?? p.invoice?.id ?? null
  const detailsCardFulfillmentType = fulfillmentTypeForCard(
    p as PrescriptionWithTypeField,
  )

  return makeRxCardItem({
    subType: 'PRESCRIPTION',
    date,
    soapId: (p as { soapId?: string | null }).soapId ?? null,
    businessId: (p as { businessId?: string | null }).businessId ?? null,
    invoiceId,
    vet,
    controlled: (p as { controlled?: boolean }).controlled ?? false,
    prescription: {
      ...p,
      stateId,
      doctorId,
      signerId,
      doctor: prescriptionDoctor ?? undefined,
      signer: prescriptionSigner ?? undefined,
      ...(detailsCardFulfillmentType != null
        ? { prescriptionType: detailsCardFulfillmentType }
        : {}),
    } as Prescription,
    signerId: signerId ?? undefined,
    cancellationReasonDetail:
      (p as { cancellationReasonDetail?: string }).cancellationReasonDetail ??
      undefined,
  })
}

/**
 * Converts PrescriptionTimelineItem or DrugTimelineItem to RxCardItem for use with BaseRxCard.
 * PTI: merges item-level name/masterName onto prescription for card title. Drug: builds prescription from drug fields.
 */
export function convertToRxCardItem(item: PrescriptionTimelineItem): RxCardItem
export function convertToRxCardItem(
  drug: DrugTimelineItem,
  options?: RxCardConversionOptions,
): RxCardItem | undefined
export function convertToRxCardItem(
  item: PrescriptionTimelineItem | DrugTimelineItem,
  options?: RxCardConversionOptions,
): RxCardItem | undefined {
  return isPrescriptionTimelineItem(item)
    ? fromPTI(item)
    : fromDrug(item, options)
}

/**
 * Invoice ID for Rx card. Use in card path only; do not add to prescriptionUtils.
 */
export function getInvoiceIdFromRxCardItem(
  item: RxCardItem,
): string | null | undefined {
  return item.invoiceId ?? item.prescription?.invoiceId
}

/**
 * Invoice ID for either PrescriptionTimelineItem or RxCardItem.
 * Use in shared hooks (e.g. usePrescriptionCardActions) so PrescriptionsCard can pass PTI.
 */
export function getInvoiceIdForCard(
  item: PrescriptionTimelineItem | RxCardItem,
): string | null | undefined {
  return (
    item.invoiceId ??
    item.prescription?.invoiceId ??
    (item as { invoice?: { id?: string } }).invoice?.id
  )
}

/** PTI has top-level name/masterName; RxCardItem does not. Use for PTI | RxCardItem only. */
function isPrescriptionTimelineItemForUtils(
  item: PrescriptionTimelineItem | RxCardItem,
): item is PrescriptionTimelineItem {
  // NOTE: This assumes RxCardItem does not have top-level name/masterName.
  return 'name' in item
}

/**
 * Returns the { name, masterName, prescription } shape expected by
 * prescriptionUtils.timelinePrescriptionToRegularPrescription (refill/print).
 */
export function convertToTimelineLikeForPrescriptionUtils(
  item: PrescriptionTimelineItem | RxCardItem,
): {
  masterName: Prescription['masterName']
  name: Prescription['name']
  prescription: Prescription
} {
  const p = item.prescription

  if (isPrescriptionTimelineItemForUtils(item)) {
    return {
      name: item.name ?? p?.name ?? '',
      masterName: item.masterName ?? p?.masterName ?? item.name ?? '',
      prescription: p,
    }
  }

  return {
    name: p?.name ?? '',
    masterName: p?.masterName ?? p?.name ?? '',
    prescription: p,
  }
}
